export const profileData = {
  name: "Uddeshya Barnwal",
  title: "AI Research Engineer",
  location: "Bangalore, India",
  email: "uddeshyabarnwal@gmail.com",
  linkedin: "https://linkedin.com/in/uddeshya-barnwal-b02510100",
  github: "https://github.com/uddeshya",
  twitter: "https://twitter.com/uddeshya",
  bio: "Building intelligent systems at the intersection of applied research and production engineering. Specialized in LLMs, agentic workflows, and scalable AI infrastructure.",
  profileImage: "/assets/images/profile-image.svg"
};

export const projects = [
  {
    id: 1,
    slug: "agentic-procurement-platform",
    title: "Agentic Procurement Platform",
    organization: "Valorant Consulting",
    role: "AI Technology Lead (Founding Hire)",
    period: "Jul 2025 - Dec 2025",
    location: "Bangalore, India",
    description: "Led the technical foundation of an AI-driven, agentic procurement platform. Designed system architecture, intake management, and built the initial engineering team from concept to functional demo.",
    tags: ["Agentic AI", "System Design", "Platform Architecture"],
    category: "Engineering",
    fullDescription: "Valorant Technology is a procurement-focused consulting firm with over 5+ years of experience delivering strategic and operational solutions across sourcing, contracting, and spend optimization. As part of its evolution from pure consulting to product-led offerings, the company initiated the development of **Astra**—a unified, AI-powered procurement platform designed to support **Intake Management** and **Spend Analytics** under a single, extensible umbrella.\n\nI joined Valorant Technology as the **first technical hire**, with a mandate to **conceptualize, architect, and deliver the technical foundation** for this next-generation, agentic procurement platform.",
    challenge: "### **Key Challenges**\n\nBuilding Astra as a first-of-its-kind, agentic procurement platform involved navigating several foundational challenges that shaped both the technical and product strategy:\n\n1. **Fragmented & Manual Procurement Ecosystems**\n\nTraditional procurement processes across organizations were highly fragmented, manual, and time-consuming. Core activities such as vendor management, quote comparison, approvals, and workflow orchestration were often handled through disconnected tools, emails, and spreadsheets—creating inefficiencies and limited visibility.\n\n2. **Highly Variable, Organization-Specific Workflows**\n\nNo two organizations followed the same procurement workflow. Each had unique approval hierarchies, compliance rules, and intake patterns, requiring the platform to be **deeply configurable and workflow-agnostic** rather than hard-coded for a single process.\n\n3. **Ambiguity Around \"Agentic AI\" Definitions**\n\nThe problem space itself was ambiguous. Stakeholders had varying interpretations of what \"agentic\" meant—ranging from simple automation to fully autonomous systems. There was no shared mental model, making it necessary to define **clear agent boundaries, human-in-the-loop controls, and realistic autonomy levels** from first principles.\n\n4. **Zero-to-One Product Creation**\n\nThere were no existing tools, codebases, or reference architectures to build upon. Everything—from ideation and system design to roadmap planning and productionization—had to be created from scratch while still aligning with enterprise procurement realities.\n\n5. **Absence of an Initial Engineering Team**\n\nAs the first technical hire, I operated without an existing engineering team or established processes. This required simultaneously acting as architect, implementer, and technical leader—while laying down engineering standards, documentation practices, and a scalable foundation for future hires.",
    approach: [
      "I led the end-to-end technical vision and execution of the Astra platform, translating high-level business intent into a scalable, production-oriented system while owning architecture, roadmap, and delivery from concept to demo.",
      {
        title: "Platform Architecture & Roadmap",
        description: "Defined the overall system architecture for an AI-driven, agentic procurement platform and designed a multi-module roadmap spanning Intake Management, Spend Analytics, and future agent-based automation, ensuring long-term extensibility, enterprise security, and seamless integration with procurement ecosystems."
      },
      {
        title: "Intake Management System Design",
        description: "Architected the core intake workflows to support structured request capture, intelligent classification, routing, and prioritization, while designing AI-assisted decision flows that reduced manual triage and translated real-world procurement processes into scalable, software-defined systems."
      },
      {
        title: "Agentic AI Strategy",
        description: "Defined the operating model for autonomous and semi-autonomous AI agents across procurement use cases such as intake triage, data enrichment, and analytics, establishing clear boundaries between autonomous execution and human-in-the-loop controls to align with enterprise governance requirements."
      },
      {
        title: "Engineering Leadership",
        description: "Built and mentored the initial engineering team while establishing coding standards, architectural guidelines, and documentation practices, acting as the primary bridge between business stakeholders and engineering to maintain clarity, alignment, and execution velocity."
      },
      {
        title: "Delivery & Demo Readiness",
        description: "Led the platform from zero-to-one through a functional, demo-ready state by prioritizing features that demonstrated tangible business value without compromising architectural integrity."
      }
    ],
    outcomes: [
      "Delivered a functional, client-facing demo within 6 months, validating the platform vision with early stakeholders.",
      "Built and mentored an initial team of 4 engineers, establishing a strong engineering culture.",
      "Finalized a production-ready technical roadmap aligned with SOC 2 compliance requirements.",
      "Established core engineering practices, including coding standards, architectural guidelines, and a finalized technology stack.",
      "Laid the technical foundation for Valorant Technology's transition from consulting-led services to a product-first procurement platform."
    ],
    techStack: ["Python", "FastAPI", "LangChain", "PostgreSQL", "Redis", "Docker", "AWS"],
    learnings: "Building a founding team requires balancing speed with quality. Early architectural decisions compound—investing in proper system design and documentation pays dividends as the team scales."
  },
  {
    id: 2,
    slug: "building-program-generator",
    title: "LLM-Powered Building Program Generator",
    organization: "Snaptrude",
    role: "AI Software Engineer II",
    period: "Oct 2024 - Mar 2025",
    location: "Bangalore, India",
    description: "Designed and implemented AI-driven building program and planning system using LLM-powered agentic workflows. Integrated spatial adjacency generation with real-time visualization in Snaptrude Canvas.",
    tags: ["Applied Research", "LLM Workflows", "System Design"],
    category: "Engineering",
    fullDescription: "Snaptrude is a collaborative building design and planning platform used by architects and planners to model complex spaces interactively. I worked on designing and implementing an AI-driven building program and planning system that leveraged LLM-powered, agentic workflows to translate high-level user inputs into structured building programs, spatial adjacencies, and visual representations—seamlessly integrated into Snaptrude's existing product ecosystem.",
    challenge: "Building program creation involves reasoning over space requirements, departmental adjacencies, and constraints such as size, relationships, and functional grouping. Traditional approaches relied on static rules and couldn't handle the ambiguity and variation in user inputs while producing usable, design-ready outputs.",
    approach: [
      "Designed LLM-powered agentic workflow to interpret user requirements and generate structured program outputs",
      "Built spatial adjacency reasoning module that produced architecturally sensible relationships between spaces",
      "Implemented REST API backend for agent orchestration and workflow management",
      "Integrated AI outputs with SnapBI for structured data management",
      "Connected spatial adjacency generation with Snaptrude Canvas (Babylon.js) for real-time visualization",
      "Enabled live updates and iterative modifications as users interacted with generated layouts"
    ],
    outcomes: [
      "Enabled automated generation of building programs from user inputs",
      "Introduced AI-driven spatial adjacency planning into core design workflows",
      "Delivered real-time visualization and update capabilities within Snaptrude Canvas",
      "Improved speed and consistency of early-stage building planning for designers",
      "Demonstrated how LLM-based systems can augment architectural workflows without disrupting established design processes"
    ],
    techStack: ["Python", "FastAPI", "LLM Agents", "JavaScript", "TypeScript", "Babylon.js", "SnapBI"],
    learnings: "LLMs are most effective in design tools when paired with clear, structured abstractions. Real-time visualization significantly improves trust and usability of AI-generated outputs. Agentic workflows work best when they support iteration, not one-shot decisions. Embedding AI deeply into existing products requires close alignment with core architecture.",
    constraints: "Product integration constraints: AI outputs had to align with Snaptrude's existing data models and visualization stack. User control vs automation: Prioritized interactive refinement over fully automated layout generation. Latency considerations: Balanced LLM reasoning depth with real-time responsiveness in the design interface."
  },
  {
    id: 3,
    slug: "nanboya-ai-systems",
    title: "Building Intelligent Systems with AI for Nanboya",
    organization: "Valuence Technology",
    role: "Artificial Intelligence Engineer",
    period: "Mar 2023 - Sep 2024",
    location: "Tokyo, Japan",
    description: "Designed and deployed production-grade AI systems across LLM-based information extraction and computer vision for a large-scale commerce platform, balancing experimentation with operational reliability.",
    tags: ["Applied Research", "LLM Fine-tuning", "Computer Vision"],
    category: "Applied Research",
    fullDescription: "Nanboya is a large-scale commerce platform where accurate extraction of structured information from images and web content directly impacts business operations. At Valuence Technology, I designed, researched, and deployed production-grade AI systems across LLM-based information extraction and computer vision, translating applied research ideas into reliable systems operating on noisy, real-world data at scale. The work required balancing experimentation with long-term maintainability, cost constraints, and operational reliability.",
    challenge: "Nanboya's workflows involved large volumes of semi-structured HTML content, high-variance product images used in operational pipelines, strong requirements for precision, consistency, and explainability, and continuous deployment needs in production environments. The challenge was not model performance alone, but building AI systems that could be trusted, monitored, and evolved over time.",
    approach: [
      "Fine-tuned LLaMA 3B using QLoRA and PEFT to adapt models efficiently under compute and cost constraints",
      "Designed prompts and schemas to enforce deterministic, schema-aligned outputs for structured information extraction",
      "Built agentic extraction workflow where reasoning was broken into smaller, inspectable steps with selective tool usage",
      "Deployed fine-tuned models as agentic LLM systems using AWS Bedrock for controlled, tool-assisted reasoning",
      "Developed YOLOv8-based object detection models tuned for real-world variability",
      "Built robust CI/CD pipeline using AWS SageMaker for automated training, validation, and deployment"
    ],
    outcomes: [
      "Deployed multiple AI systems supporting Nanboya's production workflows",
      "Improved reliability and consistency of structured data extraction from large HTML sources",
      "Enabled automated object detection across diverse, real-world image datasets",
      "Established repeatable ML deployment pipelines for faster iteration and safer releases",
      "Achieved high precision in information extraction while maintaining operational stability"
    ],
    techStack: ["LLaMA", "QLoRA", "PEFT", "Claude", "AWS Bedrock", "YOLOv8", "AWS SageMaker", "Docker", "Python"],
    learnings: "LLMs are most effective when paired with clear, structured abstractions. Real-time validation significantly improves trust in AI-generated outputs. Agentic workflows work best when they support iteration and inspection, not one-shot decisions. Building production AI systems requires equal attention to model performance, infrastructure, monitoring, and failure modes. Clear separation between model reasoning, tool execution, and validation ensures predictable behavior in production.",
    constraints: "Compute and cost constraints required efficient fine-tuning approaches. Production requirements demanded consistency and explainability over raw performance. High request volumes necessitated focus on robustness and operational stability. Integration with existing data pipelines required careful alignment with downstream services."
  },
  {
    id: 4,
    slug: "keiko-bot-rag-system",
    title: "LLM-Powered Keiko Bot & Enterprise Chatbot Platform",
    organization: "Valuence Technology",
    role: "Artificial Intelligence Engineer",
    period: "Jul 2020 - May 2023",
    location: "Tokyo, Japan",
    description: "Led design, development, and deployment of LLM-powered chatbot systems with agentic RAG pipelines, multi-tenant SaaS architecture, and enterprise-grade CI/CD across AWS and Azure.",
    tags: ["RAG", "Multi-tenant SaaS", "CI/CD"],
    category: "Applied Research",
    fullDescription: "Keiko Bot and the associated enterprise chatbot platform were designed to enable AI-powered conversational access to large volumes of organizational knowledge, across multiple documents, tenants, and communication channels. At Valuence Technology, I led the design, development, and deployment of LLM-powered chatbot systems, spanning agentic RAG pipelines, multi-tenant SaaS architecture, and enterprise-grade CI/CD across both AWS and Azure environments. The work focused on building scalable, reliable conversational systems suitable for enterprise adoption, not experimental chat interfaces.",
    challenge: "Enterprise chatbot systems faced several challenges: Knowledge scattered across multiple documents and sources, need for accurate, grounded responses rather than generic generation, multi-tenant requirements with isolated data and configurations, deployment across diverse enterprise channels and environments, and strong expectations around reliability, security, and maintainability. The core challenge was designing a system that could reason over large document sets while remaining predictable and operable at scale.",
    approach: [
      "Designed multi-document, agentic RAG architecture where queries were decomposed into retrievable sub-tasks with dynamic document chunk retrieval",
      "Introduced agentic reasoning patterns that managed context across multiple retrieval steps and controlled tool usage",
      "Implemented AI layer using OpenAI LLMs, LlamaIndex for document management, and vector databases for semantic search",
      "Built scalable, multi-tenant SaaS chatbot platform using .NET and Bot Framework with automated tenant provisioning",
      "Established robust CI/CD pipelines across AWS (SageMaker, Docker, Lambda) and Azure (Functions, Runbooks, ARM templates)",
      "Designed modular architecture allowing per-tenant knowledge isolation, flexible document updates, and controlled retrieval strategies"
    ],
    outcomes: [
      "Delivered production-ready enterprise chatbot platform serving multiple tenants",
      "Enabled accurate, grounded question-answering over large document sets",
      "Supported multiple enterprise tenants with isolated knowledge bases",
      "Reduced manual onboarding through automated provisioning pipelines",
      "Established reliable CI/CD practices across AWS and Azure environments",
      "Demonstrated that LLM-powered chatbots could be operated as serious enterprise products"
    ],
    techStack: ["OpenAI", "LlamaIndex", "RAG", "Vector Databases", ".NET", "Bot Framework", "C#", "AWS SageMaker", "Docker", "Lambda", "Azure Functions", "ARM Templates", "GitHub Actions"],
    learnings: "RAG systems benefit significantly from agentic control over retrieval. Enterprise chatbots require platform thinking beyond AI models. Multi-tenant SaaS design is as important as model quality. CI/CD discipline is essential for long-running AI products. Trust and explainability drive enterprise adoption of AI systems. This project demonstrates how LLM-based RAG systems, when paired with strong platform engineering and DevOps practices, can evolve into scalable, enterprise-ready conversational products.",
    constraints: "Accuracy vs flexibility: Prioritized grounded responses over open-ended generation. Multi-cloud complexity: Required disciplined infrastructure and deployment practices. Tenant isolation: Increased architectural complexity but ensured enterprise trust. Operational reliability: Limited experimental features in favor of predictable behavior."
  },
  {
    id: 5,
    slug: "bhogi-dish-recommendation",
    title: "LLM-Powered Dish Recommendation & Menu Extraction System",
    organization: "BHogi.ai",
    role: "Lead AI Consultant",
    period: "Oct 2023 - Mar 2024",
    location: "USA (Remote)",
    description: "Designed and deployed LLM-powered dish recommendation system with automated menu extraction pipeline, enabling personalized recommendations and structured menu data ingestion from restaurant menus.",
    tags: ["Applied Research", "LLM Reasoning", "OCR & Extraction"],
    category: "Applied Research",
    fullDescription: "BHogi.ai is a food-tech platform focused on improving user discovery and decision-making around dishes and menus. As a Lead AI Consultant, I designed and deployed an LLM-powered dish recommendation system alongside an automated menu extraction pipeline, enabling personalized recommendations and structured menu data ingestion from real-world restaurant menus. The project emphasized end-to-end delivery, from model design to production deployment, under typical startup constraints of speed, cost, and reliability.",
    challenge: "The core challenges included menus available primarily as unstructured images, high variability in menu formats, fonts, and layouts, need for personalized dish recommendations rather than generic suggestions, and tight timelines typical of early-stage product development. The goal was to build systems that were accurate enough for production use while remaining lightweight and cost-effective.",
    approach: [
      "Designed LLM-driven recommendation approach leveraging reasoning over dish attributes and user preferences",
      "Represented dishes using structured profiles (ingredients, cuisine, dietary attributes) for explainable recommendations",
      "Built Menu Extractor API that processed menu images and extracted dish names with structured outputs",
      "Implemented system using Gemini Pro for reasoning and GCP Vertex AI for scalable inference",
      "Containerized services using Docker and deployed on GCP infrastructure",
      "Designed modular APIs allowing future extension to pricing, categories, and dietary metadata"
    ],
    outcomes: [
      "Delivered personalized dish recommendation engine aligned with user preferences",
      "Enabled automated extraction of dish names from menu images",
      "Reduced manual effort required to onboard restaurant menus",
      "Provided foundation for scaling recommendation features across the platform",
      "System deployed as part of BHogi.ai's production workflow"
    ],
    techStack: ["Gemini Pro", "GCP Vertex AI", "Docker", "SQL", "LLM Reasoning", "OCR", "Python"],
    learnings: "LLMs are effective for recommendation when paired with structured representations. Image-based menu extraction requires robustness over theoretical accuracy. Production AI systems benefit from simple, extensible architectures. Consulting work demands rapid alignment between business goals and technical execution. This project demonstrates how LLM-based reasoning can be applied to real consumer-facing problems, delivering personalized experiences while operating under practical product and cost constraints.",
    constraints: "Image variability: Required tolerance for imperfect OCR and extraction. Cost constraints: LLM usage had to remain economically viable. Startup timelines: Prioritized practical functionality over exhaustive optimization. Explainability: Recommendations needed to be understandable to users."
  },
  {
    id: 6,
    slug: "knowledge-graph-rag",
    title: "LLM-Driven Knowledge Graph Construction & RAG System",
    organization: "Personal Project",
    role: "Personal Project",
    period: "2024",
    description: "Designed and implemented scalable, LLM-driven Knowledge Graph construction and RAG system converting unstructured text into canonical, schema-aligned knowledge triplets stored in Neo4j with semantic retrieval via Qdrant.",
    tags: ["Knowledge Graphs", "RAG", "Semantic Search"],
    category: "Applied Research",
    fullDescription: "This project focused on designing and implementing a scalable, LLM-driven Knowledge Graph (KG) construction and Retrieval-Augmented Generation (RAG) system from unstructured text. The system converts raw documents into canonical, schema-aligned knowledge triplets, stores them in a graph database (Neo4j), and enables semantic retrieval and question answering using a vector database (Qdrant). The work emphasized data quality, canonicalization, and retrieval efficiency, bridging applied research concepts (EDC, RAG, semantic normalization) with production-grade engineering.",
    challenge: "The core challenges addressed were: Knowledge extracted from text is noisy, inconsistent, and schema-ambiguous. Same facts expressed with different relations and entity surface forms. Large schemas make naïve LLM prompting slow and unreliable. Graph databases alone lack semantic retrieval. Vector search alone lacks explicit structure and explainability. The goal was to build a system that could reliably construct, normalize, and query knowledge at scale—while remaining fast enough for real-time RAG applications.",
    approach: [
      "Designed multi-stage pipeline inspired by Extract–Define–Canonicalize (EDC) paradigm for knowledge extraction",
      "Implemented relation and entity normalization with definition-based semantic equivalence checks",
      "Built schema-aware RAG with embedding-based retrieval over schema elements for improved latency and accuracy",
      "Designed four vector collections in Qdrant: Subject, Relation, Object, and Triplet for precision-first retrieval",
      "Implemented hybrid retrieval workflow combining Qdrant vector search with Neo4j subgraph expansion",
      "Created modular ingestion pipelines with deterministic IDs to keep graph and vector layers synchronized"
    ],
    outcomes: [
      "Built production-ready KG + RAG architecture with high-quality canonicalized knowledge ingestion",
      "Enabled fast, explainable semantic question answering with minimal hallucination",
      "Created reusable foundation for document intelligence, enterprise search, and agentic reasoning systems",
      "Achieved low RAG latency through reduced LLM calls and efficient retrieval strategies",
      "Demonstrated successful integration of LLMs, graphs, and vector search into coherent knowledge platform"
    ],
    techStack: ["Python", "LLMs", "Neo4j", "Qdrant", "Sentence Embeddings", "RAG", "Knowledge Graphs"],
    learnings: "Knowledge quality is dominated by post-extraction refinement, not extraction itself. Definitions are more reliable than surface strings for semantic alignment. Vector search and graph traversal are complementary, not competing. Good RAG systems require schema discipline, not just embeddings. LLMs are most effective when used as judges, not databases. This project demonstrates the ability to translate research ideas (EDC, KG-RAG) into real systems, design scalable knowledge infrastructures, balance AI capability with engineering constraints, and build explainable, trustworthy LLM applications.",
    constraints: "LLM extraction noise required strong post-processing. Canonicalization favored precision over recall. Multi-collection vector design increased complexity but improved retrieval control. Graph expansion depth balanced against latency. Design decisions were guided by real-world RAG performance, not theoretical purity."
  },
  {
    id: 7,
    slug: "vakyaa-knowledge-workspace",
    title: "Vakyaa — AI-Native Knowledge Workspace & Collaboration Platform",
    organization: "Personal Project",
    role: "Personal Project",
    period: "Mar 2025 - Jul 2025",
    description: "Designed and built AI-native knowledge workspace unifying document management, structured note-taking, and context-aware AI assistance with RAG-powered copilot grounded in user-owned data.",
    tags: ["Knowledge Systems", "RAG Copilot", "Collaboration Platform"],
    category: "Applied Research",
    fullDescription: "Vakyaa is an AI-native knowledge workspace designed to unify document management, structured note-taking, and context-aware AI assistance within personal and collaborative workspaces. The platform enables users to organize files and notes in workspace-scoped, folder-based structures, automatically enrich knowledge using AI, and interact with their content through a RAG-powered copilot grounded strictly in user-owned data. Vakyaa bridges personal knowledge management and team collaboration, emphasizing structure, ownership, and explainability over generic AI chat experiences.",
    challenge: "Modern knowledge work is fragmented across documents, notes, and tools, leading to poor retrieval and cognitive overload. Key challenges: Knowledge scattered across files without semantic structure, manual organization doesn't scale, traditional file systems lack contextual understanding, generic AI chat tools not grounded in user-owned data, and collaboration tools blur ownership and accountability. The goal was to design a workspace-centric knowledge system where AI assists with organization, retrieval, and authoring—without sacrificing structure, control, or trust.",
    approach: [
      "Implemented AI-enhanced folder-based file system with automatic content parsing, semantic folder suggestions, and recursive creation",
      "Built folder-based notes system with rich text editing, inline AI assistance (Ctrl+K style), and automatic note population",
      "Integrated context-aware RAG-driven copilot scoped to workspace/folder/files with grounded reasoning",
      "Designed workspace architecture supporting personal and shared spaces with role-based access control (Viewer, Editor, Admin)",
      "Created admin panel for workspace management with email invitations and permission controls",
      "Implemented publishing system for individual notes and entire workspaces with SEO-friendly slugs",
      "Built modular backend with Node.js/TypeScript, PostgreSQL/Prisma, AWS S3, and Python AI services"
    ],
    outcomes: [
      "Built full-stack, AI-native knowledge platform from scratch",
      "Delivered scalable personal and collaborative workspaces with clear ownership boundaries",
      "Enabled grounded AI assistance over user-owned documents with minimal hallucination",
      "Established foundation for future extensions: webpage bookmarking, video ingestion, agentic workflows",
      "Demonstrated how AI can augment structured systems rather than replace them"
    ],
    techStack: ["Node.js", "TypeScript", "Express", "PostgreSQL", "Prisma", "AWS S3", "Python", "LLMs", "RAG", "Vector Embeddings", "Clerk Auth"],
    learnings: "AI is most effective when embedded into structured workflows. Folder hierarchies remain valuable when enhanced with semantics. RAG systems must respect ownership and access boundaries. Collaboration requires explicit role modeling. AI UX quality matters as much as model capability. Vakyaa reflects the ability to design AI-native product architectures end-to-end, combine full-stack engineering with applied AI systems, build scalable and extensible platforms, and translate abstract AI capabilities into real user workflows. It showcases ownership across product vision, backend engineering, AI integration, and system design.",
    constraints: "Soft deletes to prevent accidental data loss. Parsed content stored separately to avoid recomputation. AI calls isolated behind service boundaries. Workspace-scoped queries to prevent data leakage. Designed for future async pipelines (queues, background jobs). Performance and reliability considerations guided all architectural decisions."
  },
  {
    id: 8,
    slug: "protein-interaction-prediction",
    title: "Protein-Protein Interaction Prediction",
    organization: "IIT Kharagpur",
    role: "Master's Thesis Project",
    period: "2019 - 2020",
    description: "Deep learning approach using CNN and LSTM/GRU layers for predicting protein-protein interactions. Achieved 95.45% accuracy on Human dataset and 96.17% on E. Coli dataset, demonstrating robustness across biological datasets.",
    tags: ["Deep Learning", "Bioinformatics", "Research"],
    category: "Applied Research",
    fullDescription: "Developed a deep learning model to predict protein-protein interactions from sequence data, addressing a fundamental challenge in computational biology with applications in drug discovery and disease understanding.",
    challenge: "Protein-protein interactions are crucial for understanding biological processes and drug targets. Experimental validation is expensive and time-consuming. Computational prediction methods can accelerate discovery, but existing approaches struggled with accuracy and generalization across species.",
    approach: [
      "Designed hybrid neural architecture combining CNNs for local pattern recognition and LSTM/GRU for sequential dependencies",
      "Developed custom protein sequence encoding scheme to capture biochemical properties",
      "Implemented attention mechanisms to identify critical interaction sites",
      "Applied transfer learning across species to improve model generalization",
      "Extensive hyperparameter tuning using Adam optimizer and careful regularization"
    ],
    outcomes: [
      "Achieved 95.45% accuracy on Human PPI dataset (94.51% recall, 96.25% precision)",
      "96.17% accuracy with LSTM and 94.46% with GRU on E. Coli dataset",
      "Demonstrated strong cross-species generalization capability",
      "Model predictions aligned with known biological interaction patterns",
      "Results validated against experimental data from protein interaction databases"
    ],
    techStack: ["Python", "TensorFlow", "Keras", "NumPy", "Pandas", "Scikit-learn", "BioPython"],
    learnings: "Deep learning in biology requires careful consideration of domain knowledge. The choice of encoding scheme and architecture must reflect biological principles. Cross-validation across different species proved essential for assessing true model generalization rather than dataset memorization."
  },
  {
    id: 9,
    slug: "perpule-internship",
    title: "Data Science Intern",
    organization: "Perpule",
    role: "Data Science Intern",
    period: "May 2019 - Jul 2019",
    location: "Bangalore, India",
    description: "Built and deployed scalable recommendation engine for 700+ retail stores using matrix factorization (SVD), supported by automated data pipelines. Developed analytical models for customer segmentation (RFM) and market basket analysis.",
    tags: ["Recommendation Systems", "Data Science", "Analytics"],
    category: "Engineering"
  },
  {
    id: 10,
    slug: "vedantu-internship",
    title: "Product Marketing Analyst Intern",
    organization: "Vedantu",
    role: "Product Marketing Analyst Intern",
    period: "May 2018 - Jul 2018",
    location: "Bangalore, India",
    description: "Analyzed website and marketing funnels using Google Analytics to identify lead leakage and optimize conversion strategies. Designed lead-generation funnel and built predictive segmentation models to target high-intent students.",
    tags: ["Marketing Analytics", "Predictive Modeling", "Funnel Optimization"],
    category: "Engineering"
  }
];

export const aboutSection = {
  title: "Applied Research ↔ Engineering",
  subtitle: "Building reliable AI systems by translating theory into production reality.",
  image: "/assets/images/about-image.jpeg",
  research: {
    title: "Applied Research",
    subtitle: "Turning theory into usable intelligence",
    points: [
      "Formulate problems from ambiguous business requirements into tractable AI system designs",
      "Translate cutting-edge research (RAG, agentic workflows, multi-step reasoning) into practical architectures",
      "Design evaluation frameworks that measure what matters to users, not just model metrics",
      "Reason about model limitations, failure modes, and the boundaries of what's feasible"
    ]
  },
  engineering: {
    title: "Engineering",
    subtitle: "Production-grade AI, infra-aware, reliable",
    points: [
      "Build backend-first AI systems with FastAPI, proper async patterns, and scalable data pipelines",
      "Design multi-tenant, workspace-aware architectures that handle real-world access control and isolation",
      "Implement observability, monitoring, and graceful degradation for production reliability",
      "Optimize for maintainability and iteration speed, not just initial delivery"
    ]
  },
  narrative: "I have 5+ years of experience building AI systems at the intersection of applied research and production engineering. My work involves translating research ideas—whether from papers, experiments, or domain expertise—into reliable, scalable products that solve real problems. I focus on systems thinking: understanding not just how models work, but how they fit into broader technical and organizational contexts. From agentic platforms to LLM-powered workflows, I build systems that are designed to fail gracefully, scale predictably, and remain maintainable as requirements evolve.",
  stats: [
    { value: "05+", label: "Years — Applied AI Systems" },
    { value: "20+", label: "Projects — Research → Production" },
    { value: "04+", label: "Organizations — Startup to Enterprise" }
  ],
  resumeUrl: "/assets/resume/UddeshyaBarnwal.pdf"
};

export const technicalCapabilities = {
  aiMlSystems: {
    title: "AI / ML Systems",
    description: "Designing applied AI systems under real-world constraints.",
    capabilities: [
      "Retrieval-Augmented Generation (RAG) for long-lived knowledge",
      "Agentic workflows and multi-agent (A2A) coordination",
      "LLM orchestration, tool calling, and control flow",
      "Prompt and context engineering for reliability and grounding",
      "Model evaluation, benchmarking, and failure analysis",
      "End-to-end AI system design (research → production)"
    ]
  },
  frameworksProtocols: {
    title: "Frameworks & Protocols",
    purpose: "Accelerating development while maintaining architectural clarity.",
    items: [
      "LangChain",
      "LlamaIndex",
      "PyTorch Lightning",
      "TensorFlow Extended (TFX)",
      "Agent-to-Agent (A2A) protocols",
      "Tool-calling frameworks",
      "Workflow orchestration frameworks"
    ]
  },
  backendApis: {
    title: "Backend & APIs",
    purpose: "Serving, orchestration, and system integration.",
    items: [
      "FastAPI",
      "Node.js",
      "REST & internal service APIs",
      "Async processing & background workers"
    ]
  },
  dataStorage: {
    title: "Data & Storage",
    purpose: "Persistence, retrieval, and scalable knowledge access.",
    items: [
      "PostgreSQL",
      "MongoDB",
      "Vector databases",
      "Qdrant"
    ]
  },
  infrastructureCloud: {
    title: "Infrastructure & Cloud",
    purpose: "Deployment, reliability, and long-running system operations.",
    items: [
      "Docker",
      "AWS",
      "Azure",
      "GCP",
      "CI/CD pipelines",
      "Monitoring, logging, and reliability"
    ]
  },
  frontend: {
    title: "Frontend (Supporting)",
    purpose: "Enabling AI interactions, not UI-first development.",
    items: [
      "React",
      "Next.js",
      "Streamlit"
    ]
  },
  languages: {
    title: "Languages",
    purpose: "Core implementation and system glue.",
    items: [
      "Python",
      "TypeScript",
      "JavaScript"
    ]
  }
};

export const writings = [
  {
    id: 1,
    title: "1. Building Agentic Platforms (Not AI Demos)",
    date: "Jan 19, 2026",
    excerpt: "From demo agents to production platforms: why agentic systems are fundamentally systems engineering problems.",
    status: "published",
    link: "https://uddeshyabarnwal.substack.com/p/building-agentic-platforms-not-ai"
  },
  {
    id: 2,
    title: "2. The Runtime System Behind Every Production AI Agent",
    date: "Jan 19, 2026",
    excerpt: "Why runtime systems and control planes are the real engine behind reliable agentic platforms.",
    status: "published",
    link: "https://uddeshyabarnwal.substack.com/p/2-the-runtime-system-behind-every"
  },
  {
    id: 3,
    title: "3. Inside the LLM Control Plane",
    date: "Jan 21, 2026",
    excerpt: "A technical walkthrough of request normalization, policy evaluation, routing, execution, and telemetry.",
    status: "published",
    link: "https://uddeshyabarnwal.substack.com/p/inside-the-llm-control-plane"
  },
  {
    id: 4,
    title: "4. Cost Is a Runtime Constraint, Not a Finance Problem",
    date: "Jan 25, 2026",
    excerpt: "How to move cost into runtime decisions using admission control, CTL, and cost-aware routing.",
    status: "published",
    link: "https://uddeshyabarnwal.substack.com/p/4-cost-is-a-runtime-constraint-not"
  },
  {
    id: 5,
    title: "5. Caching in LLM Systems (Why It's Not Just Redis)",
    date: "Jan 25, 2026",
    excerpt: "Designing correctness-preserving caches with canonical hashing, policy-driven reuse, and safe TTLs.",
    status: "published",
    link: "https://uddeshyabarnwal.substack.com/p/5-caching-in-llm-systems-why-its"
  },
  {
    id: 6,
    title: "6. Observability - Traces, p95, and Knowing Why Agents Misbehave",
    date: "Jan 26, 2026",
    excerpt: "How to instrument agentic systems with traces, inflation metrics, and cost-velocity monitoring.",
    status: "published",
    link: "https://uddeshyabarnwal.substack.com/p/6-observability-traces-p95-and-knowing"
  },
  {
    id: 7,
    title: "7. Reliability - Failover, Model Switching, and Graceful Degradation",
    date: "Jan 26, 2026",
    excerpt: "Building resilient agentic platforms with adapters, circuit breakers, and graceful degradation.",
    status: "published",
    link: "https://uddeshyabarnwal.substack.com/p/7-reliability-failover-model-switching"
  }
];

export const journalEntries = [
  {
    id: 1,
    title: "Computational Landscapes",
    date: "2024",
    image: "https://images.unsplash.com/photo-1517694712202-14dd9538aa97",
    link: "#"
  },
  {
    id: 2,
    title: "Between Research and Reality",
    date: "2024",
    image: "https://images.unsplash.com/photo-1551033406-611cf9a28f67",
    link: "#"
  },
  {
    id: 3,
    title: "Field Notes: Tokyo",
    date: "2023",
    image: "https://images.unsplash.com/photo-1540959733332-eab4deabeeaf",
    link: "#"
  }
];

export const experience = [
  {
    title: "AI Technology Lead",
    organization: "Valorant Consulting",
    period: "Jul 2024 - Dec 2024",
    type: "Founding Hire"
  },
  {
    title: "AI Software Engineer 2",
    organization: "Snaptrude",
    period: "Oct 2023 - Mar 2024"
  },
  {
    title: "AI Engineer",
    organization: "Valuence Technology",
    location: "Tokyo, Japan",
    period: "Jul 2020 - Sep 2024"
  }
];
